/**
 * Test traumă. PTSD Checklist for DSM-5 (PCL-5).
 * Weathers FW, Litz BT, Keane TM, Palmieri PA, Marx BP, Schnurr PP (2013).
 * The PTSD Checklist for DSM-5 (PCL-5), Standard form. National Center for PTSD.
 * Development: Blevins CA et al., J Trauma Stress 2015;28(6):489-498. DOI 10.1002/jts.22059
 *
 * Licence: "This measure was developed by staff at VA's National Center for
 * PTSD and is in the public domain and not copyrighted." (ptsd.va.gov, checked
 * for seo-report/briefs/instrumente-trauma.md).
 *
 * Cut-off: the Romanian validation, Georgescu T et al., Eur J Psychotraumatol
 * 2024;15(1):2333222, DOI 10.1080/20008066.2024.2333222, PMC11073431:
 * "An optimal cut-off score of >47", sensitivity 64.71, specificity 82.09,
 * AUC .75, omega .94, on 727 trauma-exposed participants (101 with SCID-5).
 * The American 31-33 is deliberately NOT used here.
 *
 * The Romanian study does not publish its item wording, so the questions below
 * are Mindtale's translation of the official English standard form (version
 * 29 August 2023), kept item for item and in the original order, because the
 * DSM-5 clusters are read from item positions (B 1-5, C 6-7, D 8-14, E 15-20).
 */

import type { TestConfig } from "./types";

export type TraumaCategory = "below_cutoff" | "above_cutoff";

/** Scores strictly above this are above the Romanian cut-off. */
export const PCL5_RO_CUTOFF = 47;

const QUESTIONS = [
  {
    id: 1,
    text: "Amintiri repetate, tulburătoare și nedorite ale experienței stresante?",
  },
  {
    id: 2,
    text: "Vise repetate și tulburătoare despre experiența stresantă?",
  },
  {
    id: 3,
    text: "Senzația bruscă sau reacția ca și cum experiența stresantă s-ar petrece din nou, ca și cum ai fi din nou acolo și ai retrăi-o?",
  },
  {
    id: 4,
    text: "Supărare puternică atunci când ceva îți amintea de experiența stresantă?",
  },
  {
    id: 5,
    text: "Reacții fizice puternice când ceva îți amintea de experiența stresantă, de exemplu inima care bate tare, greutate în respirație sau transpirație?",
  },
  {
    id: 6,
    text: "Evitarea amintirilor, gândurilor sau emoțiilor legate de experiența stresantă?",
  },
  {
    id: 7,
    text: "Evitarea a ceea ce îți amintește de experiența stresantă, de exemplu oameni, locuri, conversații, activități, obiecte sau situații?",
  },
  {
    id: 8,
    text: "Dificultăți în a-ți aminti părți importante ale experienței stresante?",
  },
  {
    id: 9,
    text: "Convingeri negative puternice despre tine, despre ceilalți sau despre lume, de exemplu gânduri ca „sunt rău/rea”, „e ceva grav în neregulă cu mine”, „nu pot avea încredere în nimeni”, „lumea e complet periculoasă”?",
  },
  {
    id: 10,
    text: "Învinovățirea ta sau a altcuiva pentru experiența stresantă sau pentru ce s-a întâmplat după ea?",
  },
  {
    id: 11,
    text: "Emoții negative puternice, cum ar fi frică, groază, furie, vinovăție sau rușine?",
  },
  {
    id: 12,
    text: "Pierderea interesului pentru activități care îți plăceau înainte?",
  },
  {
    id: 13,
    text: "Senzația că ești distant/ă sau rupt/ă de ceilalți oameni?",
  },
  {
    id: 14,
    text: "Dificultăți în a simți emoții pozitive, de exemplu să nu poți simți bucurie sau afecțiune pentru oamenii apropiați?",
  },
  {
    id: 15,
    text: "Iritabilitate, izbucniri de furie sau comportament agresiv?",
  },
  {
    id: 16,
    text: "Asumarea unor riscuri prea mari sau lucruri care ți-ar putea face rău?",
  },
  {
    id: 17,
    text: "Starea de „hiper-alertă”, în care stai la pândă sau în gardă?",
  },
  {
    id: 18,
    text: "Senzația că tresari ușor sau că te sperii repede?",
  },
  {
    id: 19,
    text: "Dificultăți de concentrare?",
  },
  {
    id: 20,
    text: "Dificultăți în a adormi sau în a dormi fără să te trezești?",
  },
];

/* The official anchors: Not at all, A little bit, Moderately, Quite a bit, Extremely. */
const OPTIONS = [
  { label: "Deloc", value: 0 },
  { label: "Puțin", value: 1 },
  { label: "Moderat", value: 2 },
  { label: "Destul de mult", value: 3 },
  { label: "Extrem de mult", value: 4 },
];

const RESOURCE_URL =
  "https://mindtale.ro/trauma-si-ptsd?utm_source=email&utm_medium=transactional&utm_campaign=test_trauma";
const THERAPIST_URL =
  "https://mindtale.ro/gaseste-terapeut?energy=2&utm_source=email&utm_medium=transactional&utm_campaign=test_trauma";

export const traumaConfig: TestConfig<TraumaCategory> = {
  slug: "trauma",
  totalQuestions: QUESTIONS.length,
  estimatedMinutes: 5,
  /* Not rendered by TestPageLayout; kept because the contract requires it.
     No image: the tests dropped stock portraits. */
  hero: {
    eyebrow: "20 de întrebări · 5 minute · gratuit",
    titlePart1: "Test PTSD: cât de mult te mai ",
    italicText: "afectează",
    titlePart2: " ce ai trăit?",
    subtitle:
      "Douăzeci de întrebări din PCL-5 despre ultima lună, cu pragul din validarea românească a chestionarului.",
    badges: [
      { text: "PTSD Checklist for DSM-5 (PCL-5)" },
      { text: "Prag din validarea românească" },
      { text: "Fără cont" },
    ],
    imageSrc: "",
    imageAlt: "",
  },
  questions: QUESTIONS,
  options: OPTIONS,
  scoring: {
    type: "sum",
    scoreMax: 80,
    scoreSuffix: "/80",
    allowedAnswerValues: [0, 1, 2, 3, 4],
    categorize: (answers) => {
      if (answers.length !== QUESTIONS.length) {
        throw new Error("Toate întrebările trebuie completate");
      }
      const score = answers.reduce((acc, val) => acc + val, 0);
      const category: TraumaCategory =
        score > PCL5_RO_CUTOFF ? "above_cutoff" : "below_cutoff";
      return { score, category };
    },
  },
  /* Two bands, because the Romanian study reports one threshold and nothing
     else. No green "all clear" colour for the lower band: a score under the
     threshold does not rule PTSD out. */
  bands: [
    {
      category: "below_cutoff",
      range: "0–47",
      label: "Sub pragul românesc",
      caption:
        "Nu exclude stresul posttraumatic. Dacă simptomele te afectează, merită discutate.",
      bg: "#E0B96C",
    },
    {
      category: "above_cutoff",
      range: "48–80",
      label: "Peste pragul românesc",
      caption:
        "Merită o evaluare făcută de un specialist care lucrează cu trauma.",
      bg: "#DC7050",
    },
  ],
  resultCopy: {
    below_cutoff: {
      headline: "Scorul tău e sub pragul de 47 de puncte",
      subheadline: "Asta nu înseamnă că ce ai trăit nu contează.",
      description:
        "Suma răspunsurilor tale nu trece de pragul găsit în validarea românească a PCL-5. Reacțiile tale sunt reale oricare ar fi scorul. Rezultatul arată doar că, pe acest chestionar, intensitatea lor din ultima lună e sub nivelul de la care studiul românesc a găsit cel mai des stres posttraumatic la interviul clinic.",
      meaning:
        "În studiul românesc, aproximativ una din trei persoane care aveau PTSD la interviul clinic a avut un scor de 47 sau mai mic. De aceea, un scor sub prag nu exclude stresul posttraumatic. Dacă amintirile, evitarea sau starea de alertă îți afectează somnul, relațiile sau munca, merită să vorbești despre ele cu un psihoterapeut.",
      actions: [
        "Uită-te la întrebările la care ai răspuns „Destul de mult” sau „Extrem de mult”, de exemplu coșmarurile sau somnul. Sunt un punct de plecare concret pentru o discuție cu un specialist.",
        "Poți reface testul peste o lună, cu gândul la același eveniment, ca să vezi dacă lucrurile se schimbă.",
        "Dacă ești în pericol acum sau te gândești să îți faci rău, sună la 112, disponibil oricând. TelVerde Antisuicid, 0800 801 200, e gratuit, dar răspunde doar vineri-duminică, între 19:00 și 07:00.",
      ],
      ctaLabel: "Citește ghidul despre traumă și PTSD",
      ctaTone: "soft",
      showTherapistMatch: false,
    },
    above_cutoff: {
      headline: "Scorul tău trece de pragul de 47 de puncte",
      subheadline: "E un motiv pentru o evaluare, nu un diagnostic.",
      description:
        "Suma răspunsurilor tale e peste pragul găsit în validarea românească a PCL-5. Înseamnă că reacțiile de după evenimentul la care te-ai gândit au fost intense în ultima lună, la un nivel la care o evaluare pentru stres posttraumatic are sens. Nu înseamnă că ai PTSD: diagnosticul îl pune un specialist, după o discuție în care te ascultă.",
      meaning:
        "În studiul românesc, pragul de peste 47 de puncte a separat cel mai bine persoanele cu și fără PTSD la interviul clinic, dar nu perfect: aproximativ 18 din 100 de persoane fără PTSD au trecut și ele de prag. Un psihoterapeut poate vedea ce stă în spatele scorului și, dacă e cazul, ce fel de terapie centrată pe traumă ți s-ar potrivi.",
      actions: [
        "Programează o evaluare cu un psihoterapeut care lucrează cu trauma. Poți începe cu o sesiune de cunoaștere gratuită, de 20 de minute, ca să vezi dacă vă potriviți.",
        "Întreabă psihoterapeutul dacă lucrează cu una dintre terapiile centrate pe traumă recomandate de ghidurile clinice, cum ar fi terapia cognitiv-comportamentală centrată pe traumă sau EMDR.",
        "Dacă ești în pericol acum sau te gândești să îți faci rău, sună la 112, disponibil oricând. Dacă cineva din casă îți face rău, linia ANES, 0800 500 333, e gratuită și răspunde non-stop.",
      ],
      ctaLabel: "Găsește un psihoterapeut care lucrează cu trauma",
      ctaTone: "urgent",
      showTherapistMatch: true,
    },
  },
  source: {
    name: "PTSD Checklist for DSM-5 (PCL-5)",
    citationTitle:
      "The Posttraumatic Stress Disorder Checklist for DSM-5 (PCL-5): Development and Initial Psychometric Evaluation.",
    authors: "Blevins CA, Weathers FW, Davis MT, Witte TK, Domino JL",
    journal: "Journal of Traumatic Stress",
    year: 2015,
    volume: "28(6):489-498",
    doi: "10.1002/jts.22059",
    doiUrl: "https://doi.org/10.1002/jts.22059",
    license: "Domeniu public (National Center for PTSD, SUA)",
    subsetNote:
      "Pragul de peste 47 de puncte vine din validarea românească a PCL-5 (Georgescu et al., European Journal of Psychotraumatology, 2024). Formularea întrebărilor este traducerea Mindtale a formularului oficial.",
  },
  validity: {
    sampleSize: 727,
  },
  ui: {
    primaryAccent: "#DC7050",
    badgeDot: "#DC7050",
  },
  email: {
    subjectTemplate: (score, scoreMax, label) =>
      `Rezultatul tău la PCL-5: ${score}/${scoreMax} (${label})`,
    categoryStyles: {
      below_cutoff: {
        label: "Sub pragul românesc",
        range: "0–47",
        accent: "#A87D5C",
        barBg: "#F4E8D9",
        barFg: "#264653",
        ctaLabel: "Citește ghidul despre traumă și PTSD",
        ctaUrl: RESOURCE_URL,
      },
      above_cutoff: {
        label: "Peste pragul românesc",
        range: "48–80",
        accent: "#B85037",
        barBg: "#FBEAD5",
        barFg: "#264653",
        ctaLabel: "Găsește un psihoterapeut care lucrează cu trauma",
        ctaUrl: THERAPIST_URL,
      },
    },
  },
  /* No emailStarter on purpose: self-help exercises for trauma were not
     reviewed by a clinician, and the wrong exercise can bring memories up
     without support. Add one only after review. */
  therapistCTA: {
    utmMedium: "test_trauma",
    triggerCategories: ["above_cutoff"],
    blocks: {
      above_cutoff: {
        headline: "Merită să vorbești cu un psihoterapeut care lucrează cu trauma.",
        subline:
          "Iată câțiva psihoterapeuți care lucrează cu trauma. Prima discuție îți arată dacă vă potriviți, iar decizia de a continua e a ta.",
        ctaLabel: "Vezi psihoterapeuții",
        energyParam: 2,
      },
    },
  },
  conditionCode: {
    code: "6B40",
    name: "Post traumatic stress disorder",
    system: "ICD-11",
  },
  meta: {
    title: "Test PTSD (PCL-5) cu prag validat în România | Mindtale",
    description:
      "Chestionarul PCL-5 pentru stres posttraumatic: 20 de întrebări despre ultima lună, cu pragul din validarea românească (Georgescu et al., 2024). Gratuit, fără cont. Nu e un diagnostic.",
    canonicalUrl: "https://mindtale.ro/teste/trauma",
    keywords: [
      "test ptsd",
      "test trauma",
      "pcl-5",
      "pcl-5 romana",
      "test stres posttraumatic",
      "test ptsd online",
      "test ptsd gratuit",
      "chestionar ptsd",
      "am ptsd test",
      "test traume emotionale",
    ],
  },
  stepperFootnote:
    "Gândește-te la cel mai greu eveniment prin care ai trecut. Pentru fiecare problemă, alege cât de mult te-a deranjat în ultima lună.",
  intro: {
    eyebrow: "20 de întrebări · 5 minute · gratuit",
    title: "Gândește-te la cel mai greu\neveniment prin care ai trecut.",
    subtitle:
      "Pentru fiecare problemă, alege cât de mult te-a deranjat în ultima lună. Nu trebuie să scrii nicăieri ce s-a întâmplat.",
    ctaLabel: "Începe testul",
  },
  bandsHeader: "Cele două zone (Georgescu et al., 2024)",
};
